import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { ThemeProvider } from './contexts/ThemeContext';
import './index.css';

// Add error boundary component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Add browser compatibility check
const checkBrowserCompatibility = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isArc = ua.includes('arc/');
  
  if (isArc) {
    // Add Arc-specific meta tag for better compatibility
    const meta = document.createElement('meta');
    meta.name = 'arc-browser-compatibility';
    meta.content = 'true';
    document.head.appendChild(meta);
  }
};

// Initialize app with safety checks
const initializeApp = () => {
  try {
    checkBrowserCompatibility();
    const rootElement = document.getElementById('root');
    
    if (!rootElement) {
      throw new Error('Root element not found');
    }

    const root = createRoot(rootElement);
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <ThemeProvider>
            <App />
          </ThemeProvider>
        </ErrorBoundary>
      </StrictMode>
    );
  } catch (error) {
    console.error('Failed to initialize app:', error);
    // Fallback rendering
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div class="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div class="text-center">
            <h1 class="text-2xl font-bold mb-4">Unable to load application</h1>
            <p class="mb-4">Please try using a different browser or contact support if the problem persists.</p>
            <button onclick="window.location.reload()" class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
              Reload Page
            </button>
          </div>
        </div>
      `;
    }
  }
};

// Initialize the app
initializeApp();
